<template>
  <div class="curapick">
    <header>
      <FeatureProgressbar
        v-if="features.zeigeProgressbar?.istAktiv"
        :aktuelle-menge="features.zeigeProgressbar.aktuelleMenge"
        :maximal-menge="features.zeigeProgressbar.maximalMenge"
        :status-variante="features.zeigeProgressbar.statusVariante"
        :status-text="features.zeigeProgressbar.statusText"
        :show-prices="features.zeigePreise.istAktiv"
        :gesamtpreis-gewaehlte-produkte="features.zeigeProgressbar.gesamtpreisGewaehlteProdukte"
      />
      <ActionNavigation
        v-if="showNavigation"
        :submit-button-label="props.submitButtonLabel"
        :cancel-button-label="props.cancelButtonLabel"
        :disable-submit-button
        :submit-button-loading="isSendDecisionPending"
        @submit="showSubmitModal = true"
        @cancel="showCancelModal = true"
      />
    </header>
    <div v-if="features.zeigeHinweis?.istAktiv">
      <PflegeAlert
        v-for="(hinweis, key) in features.zeigeHinweis.hinweise"
        :key
        :variant="hinweis.statusVariante"
        :heading="hinweis.ueberschrift"
      >
        {{ hinweis.statusText }}
      </PflegeAlert>
    </div>
    <div class="main">
      <section>
        <header>
          <h2 v-if="props.customerStatus === CUSTOMER_STATUS.BESTANDSKUNDE" class="h3">
            Genehmigte Produkte
          </h2>
        </header>
        <ul>
          <li
            v-for="(masterProduct, masterProductKey, index) in products.approvedProducts"
            :key="masterProduct.sku"
            :style="`--index:${99 - index}`"
          >
            <ProductTile
              v-model="products.approvedProducts[masterProductKey]"
              :show-prices="features.zeigePreise.istAktiv"
              :default-disposable-gloves-sku
              @open-product-modal="openProductModal"
            />
          </li>
        </ul>
      </section>
      <section v-if="props.customerStatus === CUSTOMER_STATUS.BESTANDSKUNDE">
        <header>
          <h2 class="h3">Nicht genehmigte Produkte</h2>
          <button
            :title="`Nicht genehmigte Produkte ${isNotApprovedProductsWrapperVisible ? 'ausblenden' : 'einblenden'}`"
            @click="isNotApprovedProductsWrapperVisible = !isNotApprovedProductsWrapperVisible"
          >
            <PflegeIcon name="chevron-up-line" size="l" />
          </button>
        </header>
        <div
          class="notApprovedProductsWrapper"
          :class="{ active: isNotApprovedProductsWrapperVisible }"
        >
          <div>
            <PflegeAlert v-if="Object.keys(products.notApprovedProducts).length > 0" variant="info">
              <p>
                Die folgenden Produkte hat Ihre Pflegekasse derzeit nicht genehmigt. Wenn Sie eines
                dieser Produkte in Ihrer curabox Pflege benötigen, rufen Sie uns an:
              </p>
              <p>
                <a href="tel:+494087409757" title="Zum Anrufen hier klicken">
                  <PflegeButton
                    icon="phone-line"
                    label="040 / 87 40 97 57"
                    variant="secondary"
                    size="2.5rem"
                    icon-position="left"
                    class="phone"
                  />
                </a>
              </p>
              <p>
                Sie erreichen uns montags bis donnerstags von
                <b>8:00 bis 17:00 Uhr</b>
                und freitags von
                <b>8:00 bis 16:30 Uhr.</b>
              </p>
            </PflegeAlert>
            <ul>
              <li
                v-for="(masterProduct, masterProductKey, index) in products.notApprovedProducts"
                :key="masterProduct.sku"
                :style="`--index:${99 - index}`"
              >
                <ProductTile
                  v-model="products.notApprovedProducts[masterProductKey]"
                  :show-prices="features.zeigePreise.istAktiv"
                  :default-disposable-gloves-sku
                  @open-product-modal="openProductModal"
                />
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>

    <footer v-if="showNavigation">
      <ActionNavigation
        :submit-button-label="props.submitButtonLabel"
        :cancel-button-label="props.cancelButtonLabel"
        :disable-submit-button
        :submit-button-loading="isSendDecisionPending"
        @submit="showSubmitModal = true"
        @cancel="showCancelModal = true"
      />
    </footer>

    <PflegeModal
      v-model="showSubmitModal"
      is-persistent
      :heading="submitModalHeadline"
      class="submitModal"
    >
      <slot name="submitModalContent">
        <p>
          Wenn Sie Ihre Änderungen einreichen, werden Ihnen bei der nächsten (oder übernächsten)
          Lieferung die neuen Produkte in die Box gepackt.
        </p>
      </slot>
      <template #actions>
        <PflegeButton
          :label="submitModalSubmitButtonLabel"
          :disabled="disableSubmitButton"
          @click="submit"
        />
        <PflegeButton
          :label="submitModalCancelButtonLabel"
          variant="secondary"
          @click="showSubmitModal = false"
        />
      </template>
    </PflegeModal>

    <PflegeModal
      v-model="showCancelModal"
      is-persistent
      :heading="cancelModalHeadline"
      class="cancelModal"
    >
      <slot name="cancelModalContent">
        <p>
          Sie haben Änderungen an Ihrer Produktauswahl vorgenommen. Wenn Sie diese verwerfen, wird
          Ihre Box wieder auf den alten Inhalt zurückgesetzt.
        </p>
      </slot>
      <template #actions>
        <PflegeButton :label="cancelModalSubmitButtonLabel" @click="cancel" />
        <PflegeButton
          :label="cancelModalCancelButtonLabel"
          variant="secondary"
          @click="showCancelModal = false"
        />
      </template>
    </PflegeModal>

    <PflegeModal v-model="showProductModal" :heading="productModalHeading" class="productModal">
      <div v-html="productModalContent"></div>
    </PflegeModal>
  </div>
</template>

<script setup>
import { ref, toRaw, watch, computed } from 'vue'
import { decodeJwt } from 'jose'
import ProductTile from '../ProductTile/ProductTile.vue'
import FeatureProgressbar from '../FeatureProgressbar/FeatureProgressbar.vue'
import ActionNavigation from '../ActionNavigation/ActionNavigation.vue'
import { CUSTOMER_STATUS } from '../../utils/constants'
import { useDebounce, useDecisionApi, useProduct } from '../../composables'

const props = defineProps({
  authTokenContext: {
    type: String,
    required: false,
    default: ''
  },
  decisionContexts: {
    type: Array,
    required: false,
    default: () => []
  },
  cancelButtonLabel: {
    type: String,
    required: false,
    default: 'Zurücksetzen'
  },
  cancelModalHeadline: {
    type: String,
    required: false,
    default: 'Möchten Sie Ihre Änderungen verwerfen?'
  },
  cancelModalCancelButtonLabel: {
    type: String,
    required: false,
    default: 'Nein, weiter anpassen'
  },
  cancelModalSubmitButtonLabel: {
    type: String,
    required: false,
    default: 'Ja, Verwerfen'
  },
  submitButtonLabel: {
    type: String,
    required: false,
    default: 'Speichern und weiter'
  },
  submitModalHeadline: {
    type: String,
    required: false,
    default: 'Möchten Sie Ihre Änderungen jetzt einreichen?'
  },
  submitModalCancelButtonLabel: {
    type: String,
    required: false,
    default: 'Nein, weiter anpassen'
  },
  submitModalSubmitButtonLabel: {
    type: String,
    required: false,
    default: 'Ja, Auswahl abschicken'
  },
  customerStatus: {
    type: String,
    required: false,
    default: CUSTOMER_STATUS.BESTANDSKUNDE,
    validator(value) {
      return Object.values(CUSTOMER_STATUS).includes(value)
    }
  },
  showNavigation: {
    type: Boolean,
    required: false,
    default: true
  },
  initalClientDataJwt: {
    type: String,
    required: false,
    default: ''
  }
})

const emit = defineEmits({
  cancel: (cancelReason) => {
    return ['Abbruch', 'Webbrowserabbruch', 'Serverfehler'].includes(cancelReason)
  },
  error: (errorReason) => {
    return ['authTOkenExpired', 'apiError', 'apiNotAvailable'].includes(errorReason)
  },
  submit: (response) => {
    return response.products.length > 1
  }
})

const { sendDecision, isSendDecisionPending } = useDecisionApi()
const { enrichProducts, products, ausgewaehlteProdukte } = useProduct()

/* functions 4 decision loading + product loading and transform data */
const decisionJwt = ref('')
const defaultDisposableGlovesSku = ref('')
const features = ref({})

const getAusgewaehlteProdukte = (isInitialCall) => {
  if (isInitialCall && props.initalClientDataJwt) {
    const claims = decodeJwt(props.initalClientDataJwt)
    if (claims?.data?.produkte) {
      return claims.data.produkte
    }
  }
  return ausgewaehlteProdukte()
}
const decisionCall = async (contexts, enableAbortSignal) => {
  return await sendDecision(
    {
      decision: `curabox-products${props.customerStatus === CUSTOMER_STATUS.NEUKUNDE ? '-new' : ''}.v1`,
      contexts: contexts.concat([props.authTokenContext]),
      client: {
        produkte: getAusgewaehlteProdukte(!enableAbortSignal)
      }
    },
    enableAbortSignal
  )
    .then((response) => {
      if (!response.ok) {
        console.error(`decisionAPI server-error`, response)
        emit('error', 'apiError')
        return
      }
      return response.json()
    })
    .then((body) => {
      if (!body) {
        return
      }
      decisionJwt.value = body.jwt
      defaultDisposableGlovesSku.value = body.result.standardEinweghandschuh
      features.value = body.result.features
      return body
    })
    .catch((e) => {
      if (e.name === 'AbortError') {
        return
      }

      console.error('decisionAPI not available', e)
      emit('error', 'apiNotAvailable')
    })
}
const debouncedDecisionCall = useDebounce(decisionCall)

/* initial loading + watcher 4 changes */
let isInitialisationReady = false
let lastDecisionContexts = null
const loadDecisionsAndProducts = async (alternateContexts = null) => {
  products.approvedProducts = {}
  products.notApprovedProducts = {}
  lastDecisionContexts = toRaw(alternateContexts || props.decisionContexts)
  const decisionResponseData = await decisionCall(lastDecisionContexts)
  return await enrichProducts(decisionResponseData?.result?.produkte, (err) => {
    emit('error', err)
  }).finally(() => {
    isInitialisationReady = true
  })
}

watch(
  () => props.decisionContexts,
  () => loadDecisionsAndProducts()
)
watch(products, () => isInitialisationReady && debouncedDecisionCall(lastDecisionContexts, true))

const disableSubmitButton = computed(
  () =>
    isSendDecisionPending.value ||
    features.value?.zeigeProgressbar?.statusVariante === 'danger' ||
    (features.value?.zeigeProgressbar?.aktuelleMenge === 0 &&
      props.customerStatus === CUSTOMER_STATUS.NEUKUNDE)
)
const errorMessage = computed(() => {
  if (features.value?.zeigeProgressbar?.statusVariante === 'danger') {
    return 'Bitte mindern Sie ihre Produktauswahl, sodass der maximale Erstattungsbetrag nicht überschritten wird.'
  }
  if (
    features.value?.zeigeProgressbar?.aktuelleMenge === 0 &&
    props.customerStatus === CUSTOMER_STATUS.NEUKUNDE
  ) {
    return 'Bitte wählen Sie mindestens ein Produkt für Ihre Pflegehilfsmittelbox aus.'
  }
  return null
})

/* Emits */
const cancel = () => {
  emit('cancel', 'Abbruch')
  showCancelModal.value = false
  loadDecisionsAndProducts(lastDecisionContexts)
}
const submit = () => {
  showSubmitModal.value = false
  emit('submit', {
    products: toRaw(products),
    jwt: decisionJwt
  })
}

/* Exposes */
defineExpose({
  productList: products,
  submit,
  disableSubmitButton,
  errorMessage,
  isLoading: isSendDecisionPending,
  decisionJwt
})

/* Modals */
const showProductModal = ref(false)
const productModalHeading = ref('')
const productModalContent = ref('')
const openProductModal = ({ content, heading }) => {
  productModalContent.value = content
  productModalHeading.value = heading
  showProductModal.value = true
}
const showSubmitModal = ref(false)
const showCancelModal = ref(false)
const isNotApprovedProductsWrapperVisible = ref(true)

await loadDecisionsAndProducts()
</script>

<style scoped>
.curapick {
  container-type: inline-size;
  overflow: clip;
}

.curapick dialog ul {
  list-style: disc;
}
.curapick > header {
  display: grid;
  gap: 1rem;
  background-color: var(--white);
  box-shadow: 0 0.25rem 0.25rem 0 #0003;
  z-index: 99;
  position: sticky;
  top: 0;
}
.curapick > header > nav {
  display: flex;
  gap: 1rem;
  padding: 0.5rem 1rem;
}
.curapick .main {
  z-index: 1;
  position: relative;
  padding: 0 1rem 1rem;
}
.curapick .main section {
  margin-inline: auto;
  max-width: 67em;
  padding-block-end: 1em;
}
.curapick .main section > header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-block: 1rem;
}
.curapick .main section > header button {
  appearance: none;
  border: 0;
  background: none;
  color: var(--blue);
  cursor: pointer;
  transition: rotate var(--time1) ease-in-out;
}
.curapick .main section > header button[title$='einblenden'] {
  rotate: 180deg;
}
.curapick .main section .alert {
  border: 0.5rem solid var(--white);
  border-radius: 0.5rem;
  margin-block-end: 1rem;
  align-items: flex-start;
}
.curapick .main section ul {
  display: grid;
  grid-template-columns: 1fr 1fr;
  list-style-type: none;
  padding: 0;
  gap: 1rem;
  margin: 0;
}
.curapick .main section ul li {
  margin-block-end: 0;
  container-type: inline-size;
  z-index: var(--index, 0);
}
@supports not (contain: inline-size) {
  @media (max-width: 1440px) {
    .curapick .main section ul {
      grid-template-columns: 1fr;
    }
  }
}
@container (width <= 1440px) {
  .curapick .main section ul {
    grid-template-columns: 1fr;
  }
}
.curapick .main section .notApprovedProductsWrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--time2) ease-in-out;
}
.curapick .main section .notApprovedProductsWrapper > * {
  overflow: hidden;
}
.curapick .main section .notApprovedProductsWrapper.active {
  grid-template-rows: 1fr;
}
.curapick > footer {
  display: none;
}
@supports not (contain: inline-size) {
  @media (max-width: 610px) {
    .curapick .main {
      z-index: 1;
      position: relative;
    }
    .curapick > header > nav {
      display: none;
    }
    .curapick > footer {
      display: block;
      position: sticky;
      bottom: 0;
      z-index: 99;
      background: var(--white);
      box-shadow: 0 -0.25rem 0.25rem 0 #0003;
      padding: 0.5rem;
    }
  }
}
@container (width <= 600px) {
  .curapick .main {
    z-index: 1;
    position: relative;
  }
  .curapick > header > nav {
    display: none;
  }
  .curapick > footer {
    display: block;
    position: sticky;
    bottom: 0;
    z-index: 99;
    background: var(--white);
    box-shadow: 0 -0.25rem 0.25rem 0 #0003;
    padding: 0.5rem;
  }
}

/* component wide shared-components overwrites */
.curapick .main section :deep(.alert) > svg {
  --color: var(--info) !important;
}
.curapick .main section :deep(.alert) .base-button .label {
  font-size: 1em;
}
.curapick :deep(.Quantitybutton) {
  --inputSize: 2.25em;
  --borderRadius: 0.25em;
  grid-template-columns: 1.5em var(--inputSize) 1.5em auto;
  grid-template-rows: 2em auto auto;
}
.curapick :deep(.baseSelect) .baseSelect__arrowIcon {
  top: 2.625em;
  right: 1em;
}
.curapick :deep(.baseSelect) .input {
  gap: 0.25em;
  --labelHeight: 0.9em;
  --borderRadius: 0.25em;
  --inputHeight: 3em;
}
.curapick :deep(.baseSelect) .input .input-wrapper > .icon-wrapper {
  padding-inline: 0.25em;
  gap: 0.25em;
}
.curapick :deep(.baseSelect) .input .input-wrapper > :is(input, textarea) {
  padding: 0.5em 2em 0.5em 0.75em;
  font-size: 1em;
}
@media (max-width: 430px) {
  .curapick :deep(dialog) aside > footer {
    flex-direction: column;
  }
}
</style>

<style>
/* component wide shared-components overwrites */
.curapick {
  font-size: 18px;
}
.curapick .text-s {
  line-height: 1.1em;
  font-size: 0.9em;
}
.curapick .text-xs {
  line-height: 1em;
  font-size: 0.8em;
}
</style>
